import { z } from 'zod';

type CustomField = {
  type: string;
  label: string;
  required?: boolean;
  validation?: { min?: number; max?: number; message?: string };
};

function fieldSchema(field: CustomField): z.ZodTypeAny | undefined {
  const { label, required, validation } = field;
  const requiredMessage = validation?.message || `${label} is required`;

  switch (field.type) {
    case 'text':
    case 'radio': {
      const text = z.string({ required_error: requiredMessage });
      return required ? text.min(1, requiredMessage) : text.optional();
    }
    case 'boolean': {
      const flag = z.boolean({ required_error: requiredMessage });
      return required ? flag : flag.optional();
    }
    case 'number': {
      let number = z.number({
        required_error: requiredMessage,
        invalid_type_error: requiredMessage,
      });
      if (validation?.max) {
        number = number.max(
          validation.max,
          validation.message ||
            `${label} is should be less than ${validation.max}`,
        );
      }
      if (validation?.min) {
        number = number.min(
          validation.min,
          validation.message ||
            `${label} is should be greater than ${validation.min}`,
        );
      }
      /* An optional number left empty is sent as null. */
      return z.preprocess(
        value =>
          value === '' || value === undefined || value === null
            ? required
              ? undefined
              : null
            : Number(value),
        required ? number : number.nullable(),
      );
    }
    default:
      return undefined;
  }
}

/** The connection form a partner WMS asks for, from its own field list. */
export function customFieldsSchema(fields: Record<string, CustomField>) {
  const shape: Record<string, z.ZodTypeAny> = {};
  for (const [name, field] of Object.entries(fields)) {
    const schema = fieldSchema(field);
    if (schema) {
      shape[name] = schema;
    }
  }
  return z.object(shape);
}
